import { trackMetaPageView } from '@/utils/metaPixel';
import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { applyDefaultSeo, applySeo, clearStructuredData } from '@/utils/seo';

const isUnderConstruction = import.meta.env.VITE_APP_UNDER_CONSTRUCTION === 'true';

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior(to, from, savedPosition) {
    // Si hay una posición guardada (navegación con botón atrás/adelante), usarla
    if (savedPosition) {
      return savedPosition;
    }
    
    // Si es la misma ruta pero con diferentes parámetros (ej: diferentes productos)
    // scroll al inicio
    if (to.name === from.name && to.params.id !== from.params.id) {
      return { top: 0, behavior: 'smooth' };
    }
    
    // Para navegación normal (nueva página), scroll al inicio
    return { top: 0, behavior: 'smooth' };
  },
  routes: [
    {
      path: '/en-construccion',
      name: 'UnderConstruction',
      component: () => import('@/views/UnderConstructionView.vue'),
    },
    {
      path: '/',
      name: 'Home',
      component: () => import('@/views/HomeView.vue'),
      meta: {
        seo: {
          title: 'Walabi Paraguay | Hardware, Software y Accesorios',
          description: 'Compra notebooks, celulares, tablets, consolas y accesorios con entrega por delivery en Paraguay.',
        },
      },
    },
    {
      path: '/products',
      name: 'Products',
      component: () => import('@/views/ProductsView.vue'),
      meta: {
        seo: {
          title: 'Productos | Walabi Paraguay',
          description: 'Explora el catálogo completo de Walabi con notebooks, celulares, tablets, televisores, gaming y más.',
        },
      },
      // Preservar query parameters en la ruta
      beforeEnter: (_to, _from, next) => {
        // Mantener los query parameters al navegar
        next();
      }
    },
    {
      path: '/products/:slug',
      name: 'ProductDetail',
      component: () => import('@/views/ProductDetailView.vue'),
      props: true,
      meta: {
        seo: {
          title: 'Producto | Walabi Paraguay',
          description: 'Consulta especificaciones, precio y disponibilidad del producto en Walabi Paraguay.',
        },
      },
    },
    {
      path: '/categories',
      name: 'Categories',
      component: () => import('@/views/CategoriesView.vue'),
      meta: {
        seo: {
          title: 'Categorías | Walabi Paraguay',
          description: 'Navega por categorías y encuentra tecnología, accesorios y productos destacados en Walabi Paraguay.',
        },
      },
    },
    {
      path: '/cart',
      name: 'Cart',
      component: () => import('@/views/CartView.vue'),
      meta: {
        seo: {
          title: 'Carrito | Walabi Paraguay',
          description: 'Revisa los productos agregados a tu carrito antes de finalizar la compra.',
          robots: 'noindex,nofollow',
        },
      },
    },
    {
      path: '/checkout',
      name: 'Checkout',
      component: () => import('@/views/CheckoutView.vue'),
      meta: {
        seo: {
          title: 'Checkout | Walabi Paraguay',
          description: 'Completa tus datos de entrega y pago para confirmar tu pedido.',
          robots: 'noindex,nofollow',
        },
      },
    },
    {
      path: '/login',
      name: 'Login',
      component: () => import('@/views/auth/LoginView.vue'),
      meta: {
        guest: true,
        seo: {
          title: 'Iniciar sesión | Walabi Paraguay',
          description: 'Accede a tu cuenta para seguir tus pedidos, favoritos y compras.',
          robots: 'noindex,nofollow',
        },
      },
    },
    {
      path: '/register',
      name: 'Register',
      component: () => import('@/views/auth/RegisterView.vue'),
      meta: {
        guest: true,
        seo: {
          title: 'Crear cuenta | Walabi Paraguay',
          description: 'Crea tu cuenta para guardar favoritos, gestionar pedidos y comprar más rápido.',
          robots: 'noindex,nofollow',
        },
      },
    },
    {
      path: '/forgot-password',
      name: 'ForgotPassword',
      component: () => import('@/views/auth/ForgotPasswordView.vue'),
      meta: {
        guest: true,
        seo: {
          title: 'Recuperar contraseña | Walabi Paraguay',
          description: 'Solicita el enlace para restablecer la contraseña de tu cuenta.',
          robots: 'noindex,nofollow',
        },
      },
    },
    {
      path: '/reset-password',
      name: 'ResetPassword',
      component: () => import('@/views/auth/ResetPasswordView.vue'),
      meta: {
        guest: true,
        seo: {
          title: 'Restablecer contraseña | Walabi Paraguay',
          description: 'Define una nueva contraseña para volver a acceder a tu cuenta.',
          robots: 'noindex,nofollow',
        },
      },
    },
    {
      path: '/profile',
      name: 'Profile',
      component: () => import('@/views/ProfileView.vue'),
      meta: {
        requiresAuth: true,
        seo: {
          title: 'Mi perfil | Walabi Paraguay',
          description: 'Administra tus datos personales, direcciones y preferencias de compra.',
          robots: 'noindex,nofollow',
        },
      },
    },
    {
      path: '/orders',
      name: 'Orders',
      component: () => import('@/views/OrdersView.vue'),
      meta: {
        requiresAuth: true,
        seo: {
          title: 'Mis pedidos | Walabi Paraguay',
          description: 'Consulta el estado e historial de tus pedidos.',
          robots: 'noindex,nofollow',
        },
      },
    },
    {
      path: '/favorites',
      name: 'Favorites',
      component: () => import('@/views/FavoritesView.vue'),
      meta: {
        seo: {
          title: 'Favoritos | Walabi Paraguay',
          description: 'Revisa los productos que guardaste para comparar o comprar despues.',
          robots: 'noindex,nofollow',
        },
      },
    },
    {
      path: '/compare',
      name: 'Compare',
      component: () => import('@/views/CompareView.vue'),
      meta: {
        seo: {
          title: 'Comparar productos | Walabi Paraguay',
          description: 'Compara especificaciones y precios entre varios productos del catalogo.',
          robots: 'noindex,nofollow',
        },
      },
    },
    {
      path: '/api-docs',
      name: 'ApiDocumentation',
      component: () => import('@/views/ApiDocumentationView.vue'),
      meta: {
        seo: {
          title: 'API Docs | Walabi Paraguay',
          description: 'Documentacion tecnica de integracion de la tienda.',
          robots: 'noindex,nofollow',
        },
      },
    },
    {
      path: '/auth/apple/callback',
      name: 'AppleCallback',
      component: () => import('@/views/auth/AppleCallbackView.vue'),
      meta: {
        seo: {
          title: 'Apple Login | Walabi Paraguay',
          description: 'Procesando autenticacion con Apple.',
          robots: 'noindex,nofollow',
        },
      },
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicy',
      component: () => import('@/views/PrivacyPolicyView.vue'),
      meta: {
        seo: {
          title: 'Política de privacidad | Walabi Paraguay',
          description: 'Conoce como Walabi trata y protege tus datos personales conforme a la normativa aplicable en Paraguay.',
        },
      },
    },
    {
      path: '/terms-and-conditions',
      name: 'TermsAndConditions',
      component: () => import('@/views/TermsAndConditionsView.vue'),
      meta: {
        seo: {
          title: 'Términos y condiciones | Walabi Paraguay',
          description: 'Consulta los términos y condiciones aplicables al uso de la tienda y a las compras realizadas en Walabi.',
        },
      },
    },
    {
      path: '/returns-policy',
      name: 'ReturnsPolicy',
      component: () => import('@/views/ReturnsPolicyView.vue'),
      meta: {
        seo: {
          title: 'Política de devoluciones | Walabi Paraguay',
          description: 'Revisa las condiciones de cambios, devoluciones y garantías aplicables a tus compras en Walabi.',
        },
      },
    },
    {
      path: '/shipping-and-delivery',
      name: 'ShippingAndDelivery',
      component: () => import('@/views/ShippingAndDeliveryView.vue'),
      meta: {
        seo: {
          title: 'Envíos y entregas | Walabi Paraguay',
          description: 'Información sobre entregas por delivery, plazos estimados y cobertura de envíos de Walabi.',
        },
      },
    },
    {
      path: '/help-center',
      name: 'HelpCenter',
      component: () => import('@/views/HelpCenterView.vue'),
      meta: {
        seo: {
          title: 'Centro de ayuda | Walabi Paraguay',
          description: 'Resuelve dudas sobre compras, pagos, entregas, devoluciones y canales de atención de Walabi.',
        },
      },
    },
    {
      path: '/account-deletion',
      name: 'AccountDeletion',
      component: () => import('@/views/AccountDeletionView.vue'),
      meta: {
        seo: {
          title: 'Eliminación de cuenta | Walabi Paraguay',
          description: 'Solicita la eliminación de tu cuenta y conoce el tratamiento de tus datos.',
          robots: 'noindex,nofollow',
        },
      },
    },
    {
      path: '/payment/callback',
      name: 'PaymentCallback',
      component: () => import('@/views/PaymentCallbackView.vue'),
      meta: {
        seo: {
          title: 'Resultado de pago | Walabi Paraguay',
          description: 'Procesando la confirmacion del pago.',
          robots: 'noindex,nofollow',
        },
      },
    },
    {
      path: '/pago/resultado/:hash?',
      name: 'ResultadoPago',
      component: () => import('@/views/ResultadoPago.vue'),
      meta: {
        seo: {
          title: 'Resultado de pago | Walabi Paraguay',
          description: 'Consulta el estado de tu pago.',
          robots: 'noindex,nofollow',
        },
      },
    },
    {
      path: '/gracias-por-su-compra',
      alias: ['/gracias-por-su-compra/', '/index.php/gracias-por-su-compra', '/index.php/gracias-por-su-compra/'],
      name: 'GraciasPorSuCompra',
      component: () => import('@/views/GraciasPorSuCompra.vue'),
      meta: {
        seo: {
          title: 'Gracias por tu compra | Walabi Paraguay',
          description: 'Tu pedido fue recibido correctamente.',
          robots: 'noindex,nofollow',
        },
      },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: () => import('@/views/NotFoundView.vue'),
      meta: {
        seo: {
          title: 'Página no encontrada | Walabi Paraguay',
          description: 'La página que buscas no existe o fue movida.',
          robots: 'noindex,nofollow',
        },
      },
    },
  ],
});

// Navigation guards
router.beforeEach(async (to, _from, next) => {  
  const authStore = useAuthStore();
  
  if (isUnderConstruction) {
    if (to.name !== 'UnderConstruction') {
      next({ name: 'UnderConstruction' });
      return;
    }
  } else if (to.name === 'UnderConstruction') {
    next({ name: 'Home' });
    return;
  }

  // Load user if token exists but user is not loaded
  if (authStore.token && !authStore.user) {
    await authStore.loadUser();
  }

  // Check if route requires authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'Login', query: { redirect: to.fullPath } });
    return;
  }

  // Redirect authenticated users away from guest-only pages
  if (to.meta.guest && authStore.isAuthenticated) {
    next({ name: 'Home' });
    return;
  }
  
  // Handle Apple login callback
  if (to.name === 'AppleCallback') {
    // The callback component will handle the authentication
    next();
    return;
  }

  next();
});

// Preserve scroll position and filters on page reload
router.beforeResolve((to, _from, next) => {
  // Save current route state to sessionStorage for reload persistence
  if (to.name === 'Products' && Object.keys(to.query).length > 0) {
    sessionStorage.setItem('lastProductsRoute', JSON.stringify({
      path: to.path,
      query: to.query,
      timestamp: Date.now()
    }));
  }
  next();
});

router.afterEach((to, _from, failure) => {
  if (failure) return;
  trackMetaPageView(to.path);
  const seo = to.meta.seo as { title?: string; description?: string; robots?: string } | undefined

  if (!seo?.title || !seo?.description) {
    applyDefaultSeo()
  } else {
    clearStructuredData()
    applySeo({
      title: seo.title,
      description: seo.description,
      path: to.fullPath,
      robots: seo.robots,
    })
  }

  if (typeof window.gtag === 'function') {
    window.gtag('config', 'G-399KS0FSJH', {
      page_path: to.fullPath,
      page_location: window.location.href,
      page_title: document.title,
    })
  }
})

export default router;
